import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";

/**
 * ParticleDissolve Transition
 * - DRAMATIC transition for special pages (Drops, VIP, Exclusive)
 * - Current page dissolves into gold particles
 * - Particles swirl and reform into new page
 * - Duration: 900ms
 * - Most luxurious and intentional
 */

const ParticleDissolve = ({ children, isReady }) => {
  const [showParticles, setShowParticles] = useState(true);
  const controls = useAnimation();
  const canvasRef = useRef(null);

  useEffect(() => {
    // Hide particles after animation completes
    const timer = setTimeout(() => {
      setShowParticles(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Particle system
    const particles = [];
    const particleCount = 80;

    class Particle {
      constructor() {
        this.reset();
        this.initialY = Math.random() * canvas.height;
        this.y = this.initialY;
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = -10;
        this.size = Math.random() * 4 + 2;
        this.speedY = Math.random() * 3 + 2;
        this.speedX = (Math.random() - 0.5) * 2;
        this.opacity = 1;
        this.life = 100;
        // Gold color variations
        this.hue = 45 + Math.random() * 15; // Gold hues
        this.saturation = 70 + Math.random() * 30;
        this.lightness = 50 + Math.random() * 30;
      }

      update() {
        // Swirl motion
        this.x += this.speedX + Math.sin(this.y * 0.01) * 0.5;
        this.y += this.speedY;

        // Fade out
        this.life -= 1;
        this.opacity = this.life / 100;

        // Reset if off screen or dead
        if (this.y > canvas.height || this.life <= 0) {
          this.reset();
        }
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;

        // Glow effect
        ctx.shadowBlur = 15;
        ctx.shadowColor = `hsl(${this.hue}, ${this.saturation}%, ${this.lightness}%)`;

        // Main particle
        ctx.fillStyle = `hsl(${this.hue}, ${this.saturation}%, ${this.lightness}%)`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();

        // Inner bright core
        ctx.fillStyle = `hsl(${this.hue}, ${this.saturation}%, ${this.lightness + 20}%)`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Animation loop
    let animationFrameId;
    let frameCount = 0;
    const maxFrames = 90; // 900ms at 60fps

    const animate = () => {
      if (frameCount >= maxFrames) {
        cancelAnimationFrame(animationFrameId);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Gradient background overlay
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width / 2
      );
      gradient.addColorStop(0, "rgba(212, 175, 55, 0.03)");
      gradient.addColorStop(1, "rgba(212, 175, 55, 0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      frameCount++;
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <>
      {/* Particle Canvas Overlay */}
      {showParticles && (
        <motion.div
          className="particle-dissolve-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <canvas ref={canvasRef} className="particle-canvas" />
        </motion.div>
      )}

      {/* Page Content with dramatic entrance */}
      <motion.div
        className="page-transition-container"
        initial={{
          opacity: 0,
          scale: 0.95,
          rotateX: 5,
          filter: "blur(10px) brightness(1.2)"
        }}
        animate={{
          opacity: isReady ? 1 : 0.85,
          scale: 1,
          rotateX: 0,
          filter: "blur(0px) brightness(1)"
        }}
        exit={{
          opacity: 0,
          scale: 1.05,
          filter: "blur(10px) brightness(0.8)"
        }}
        transition={{
          duration: 0.6,
          delay: 0.3, // Delay so content appears as particles settle
          ease: [0.6, 0.01, 0.05, 0.95] // Dramatic expo easing
        }}
        style={{
          transformPerspective: 1000,
        }}
      >
        {children}
      </motion.div>
    </>
  );
};

export default ParticleDissolve;
